import React, { useContext, useState, useEffect, useRef } from "react";
import {
  Avatar,
  Card,
  CardContent,
  Container,
  Stack,
  SvgIcon,
  Tooltip,
  tooltipClasses,
  Typography as MuiTypography,
  Box,
  styled,
} from "@mui/material";
import Grid from "@material-ui/core/Grid";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

// Ícones
import GroupsIcon from "@mui/icons-material/Groups";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import MarkUnreadChatAltIcon from "@mui/icons-material/MarkUnreadChatAlt";
import PauseCircleFilledOutlinedIcon from "@mui/icons-material/PauseCircleFilledOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ChatIcon from "@mui/icons-material/Chat";
import AlarmOnIcon from "@mui/icons-material/AlarmOn";
import ThreePIcon from "@mui/icons-material/ThreeP";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import ScheduleSendIcon from "@mui/icons-material/ScheduleSend";
import SendIcon from "@material-ui/icons/Send";
import InfoIcon from "@mui/icons-material/Info";

import { grey } from "@material-ui/core/colors";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  container: {
    margin: "20px 10px!important",
    padding: "10px",
    background: "#fff",
    borderRadius: "10px",
    width: "calc(100% - 20px)!important",
    maxWidth: "100%!important",
    minHeight: "calc(100vh - 100px)",
  },
  overline: {
    fontSize: "0.9rem",
    fontWeight: 700,
    color: "grey",
    letterSpacing: "0.5px",
    lineHeight: 2.5,
    textTransform: "uppercase",
    fontFamily: "'Plus Jakarta Sans', sans-serif'",
  },
  h4: {
    fontFamily: "'Plus Jakarta Sans', sans-serif'",
    fontWeight: 500,
    fontSize: "2rem",
    lineHeight: 1,
    color: "grey",
  },
  cardAvatar: {
    fontSize: "55px",
    color: grey[500],
    backgroundColor: "#ffffff",
    width: theme.spacing(7),
    height: theme.spacing(7),
  },
  userState: {
    display: "inline-block",
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    marginRight: "8px",
  },
  cardContent: {
    padding: "16px !important",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
}));

const TooltipContent = styled(Box)(({ theme }) => ({
  width: "100%",
  maxHeight: "60vh",
  overflowY: "auto",
  WebkitOverflowScrolling: "touch",
}));

const TooltipTitle = styled(MuiTypography)(({ theme }) => ({
  fontWeight: "bold",
  fontSize: "14px",
  marginBottom: theme.spacing(1),
  color: theme.palette.primary.main,
}));

const UserStatusItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(1),
}));

const UserStatusGroup = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  "&:last-child": {
    marginBottom: 0,
  },
}));

const UserStatusTitle = styled(MuiTypography)(({ theme }) => ({
  fontWeight: "bold",
  marginBottom: theme.spacing(1),
}));

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip
    {...props}
    disableScrollLock
    classes={{ popper: className }}
    componentsProps={{
      tooltip: {
        sx: {
          maxHeight: "400px",
          width: "300px",
          padding: "16px",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          maxWidth: "calc(100vw - 32px)",
          overflowX: "hidden",
          touchAction: "none",
          userSelect: "none",
          WebkitTapHighlightColor: "transparent",
        },
      },
      popper: {
        modifiers: [
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              altAxis: true,
              padding: 16,
              boundariesElement: 'viewport',
            },
          },
        ],
      },
    }}
    enterTouchDelay={0}
    leaveTouchDelay={3000}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[3],
    fontSize: 14,
    borderRadius: "8px",
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.white,
  },
}));

const MobileTooltip = ({ children, title, placement = "bottom" }) => {
  const [open, setOpen] = useState(false);
  const isMobile = useRef(false);
  const tooltipRef = useRef(null);
  const childRef = useRef(null);

  // Detecção de dispositivo móvel
  useEffect(() => {
    const checkIfMobile = () => {
      isMobile.current = window.innerWidth <= 768 || 
                        /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (open && isMobile.current) {
        const tooltipElement = document.querySelector('.MuiTooltip-popper');
        if (tooltipElement && !tooltipElement.contains(event.target) && 
            childRef.current && !childRef.current.contains(event.target)) {
          setOpen(false);
        }
      }
    };

    if (open) {
      // Adiciona um pequeno delay para evitar conflito com abertura
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [open]);

  const handleTouch = (e) => {
    if (isMobile.current) {
      if (!open) {
        setOpen(true);
      }
      e.preventDefault();
    }
  };

  return (
    <LightTooltip
      title={title}
      placement={placement}
      open={open}
      onOpen={() => !isMobile.current && setOpen(true)}
      onClose={() => !isMobile.current && setOpen(false)}
      disableFocusListener={isMobile.current}
      disableHoverListener={isMobile.current}
      disableTouchListener={!isMobile.current}
      componentsProps={{
        popper: {
          onClick: (e) => e.stopPropagation() // Impede propagação para o documento
        },
        tooltip: {
          sx: {
            pointerEvents: 'auto',
            '&:hover': {
              pointerEvents: 'auto'
            }
          }
        }
      }}
    >
      <div
        ref={childRef}
        onTouchStart={handleTouch}
        onClick={handleTouch}
        style={{ display: 'inline-block', width: '100%' }}
      >
        {children}
      </div>
    </LightTooltip>
  );
};

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState({ counters: {}, average: {} });
  const [onlineUsersCount, setOnlineUsersCount] = useState(0);
  const [totalUsersCount, setTotalUsersCount] = useState(0);
  const [updatedAt, setUpdatedAt] = useState("");
  const [usersStatus, setUsersStatus] = useState({
    online: [],
    out: [],
    offline: [],
  });

  const classes = useStyles();

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const fetchTickets = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/dashboard", {
          signal: abortController.signal,
        });
        if (!isMounted) return;
        setTickets(data);
        setUpdatedAt(
          new Date().toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })
        );
      } catch (err) {
        if (!isMounted || err.name === "AbortError") return;
        console.error({ err });
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    const fetchUsers = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/usersList/", {
          signal: abortController.signal,
        });
        if (!isMounted) return;

        setTotalUsersCount(data.length);
        const onlineCount = data.filter(
          (user) => user.state === "online"
        ).length;
        setOnlineUsersCount(onlineCount);

        const onlineUsers = data
          .filter((user) => user.state === "online")
          .map((u) => ({ name: u.name, state: u.state }));
        const outUsers = data
          .filter((user) => user.state === "out")
          .map((u) => ({ name: u.name, state: u.state }));
        const offlineUsers = data
          .filter((user) => user.state === "offline")
          .map((u) => ({ name: u.name, state: u.state }));

        setUsersStatus({
          online: onlineUsers,
          out: outUsers,
          offline: offlineUsers,
        });
      } catch (err) {
        if (!isMounted || err.name === "AbortError") return;
        console.error({ err });
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
    fetchUsers();

    const intervalId = setInterval(() => {
      fetchTickets();
      fetchUsers();
    }, 60000);

    return () => {
      isMounted = false;
      abortController.abort();
      clearInterval(intervalId);
    };
  }, []);

  function isOnline(status) {
    let statusColor;

    switch (String(status).toLowerCase()) {
      case "online":
        statusColor = "#4caf50";
        break;
      case "out":
        statusColor = "#ff9800";
        break;
      default:
        statusColor = "#f44336";
        break;
    }

    return (
      <Box
        className={classes.userState}
        sx={{
          background: statusColor,
          display: "inline-block",
          width: "10px",
          height: "10px",
          borderRadius: "50%",
          marginRight: "5px",
        }}
      />
    );
  }

  const renderUserStatusTooltip = () => (
    <TooltipContent>
      <TooltipTitle>Usuário(s)</TooltipTitle>
      <Box
        sx={{
          maxHeight: "300px",
          overflow: "hidden",
          "&:hover": {
            overflowY: "auto",
          },
          pr: 1,
        }}
      >
        <UserStatusGroup>
          <UserStatusTitle>{i18n.t("dashboard.states.online")}</UserStatusTitle>
          {usersStatus.online.length > 0 ? (
            usersStatus.online.map((user, index) => (
              <UserStatusItem key={index}>
                {isOnline(user.state)}
                <Typography variant="body2">{user.name}</Typography>
              </UserStatusItem>
            ))
          ) : (
            <Typography variant="body2">
              {i18n.t("dashboard.stateInfo.noOnline")}
            </Typography>
          )}
        </UserStatusGroup>

        <UserStatusGroup>
          <UserStatusTitle>{i18n.t("dashboard.states.out")}</UserStatusTitle>
          {usersStatus.out.length > 0 ? (
            usersStatus.out.map((user, index) => (
              <UserStatusItem key={index}>
                {isOnline(user.state)}
                <Typography variant="body2">{user.name}</Typography>
              </UserStatusItem>
            ))
          ) : (
            <Typography variant="body2">
              {i18n.t("dashboard.stateInfo.noOut")}
            </Typography>
          )}
        </UserStatusGroup>

        <UserStatusGroup>
          <UserStatusTitle>
            {i18n.t("dashboard.states.offline")}
          </UserStatusTitle>
          {usersStatus.offline.length > 0 ? (
            usersStatus.offline.map((user, index) => (
              <UserStatusItem key={index}>
                {isOnline(user.state)}
                <Typography variant="body2">{user.name}</Typography>
              </UserStatusItem>
            ))
          ) : (
            <Typography variant="body2">
              {i18n.t("dashboard.stateInfo.noOffline")}
            </Typography>
          )}
        </UserStatusGroup>
      </Box>
    </TooltipContent>
  );

  const renderGenericTooltip = (value, statusIndicator = null) => {
    const items = value.split("<br />").filter((item) => item.trim() !== "");
    const tooltipTitle = statusIndicator ? "Usuário(s)" : "Fila(s)";

    return (
      <TooltipContent>
        <TooltipTitle>{tooltipTitle}</TooltipTitle>
        <Box
          sx={{
            maxHeight: "300px",
            overflow: "hidden",
            "&:hover": {
              overflowY: "auto",
            },
            pr: 1,
          }}
        >
          {items.map((item, index) => (
            <Box
              key={index}
              sx={{
                py: 0.5,
                borderBottom:
                  index < items.length - 1 ? "1px solid #f0f0f0" : "none",
                display: "flex",
                alignItems: "center",
              }}
            >
              {statusIndicator && (
                <Box
                  sx={{
                    display: "flex",
                    marginRight: "8px",
                    alignItems: "center",
                  }}
                >
                  {statusIndicator}
                </Box>
              )}
              <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                {item}
              </Typography>
            </Box>
          ))}
        </Box>
      </TooltipContent>
    );
  };

  const dashboardCards = [
    {
      title: (
        <Box display="flex" alignItems="center">
          {i18n.t("dashboard.cards.activeAttendants")}
          <InfoIcon style={{ marginLeft: 5 }} />
        </Box>
      ),
      value: `${onlineUsersCount}/${totalUsersCount}`,
      icon: <GroupsIcon />,
      color: "#805753",
      tooltip: renderUserStatusTooltip(),
    },
    {
      title: (
        <Box display="flex" alignItems="center">
          {i18n.t("dashboard.cards.ticketWithoutAttendants")}
          <InfoIcon style={{ marginLeft: 5 }} />
        </Box>
      ),
      value: tickets.counters?.ticketWithoutAttendants || 0,
      icon: <ThreePIcon />,
      color: "#8c6b19",
      tooltip: renderGenericTooltip(
        tickets.queueAndCount?.ticketWithoutAttendantsTooltip || "",
        isOnline("offline")
      ),
    },
    {
      title: (
        <Box display="flex" alignItems="center">
          {i18n.t("dashboard.cards.unread")}
          <InfoIcon style={{ marginLeft: 5 }} />
        </Box>
      ),
      value: tickets.counters?.unreadMessages || 0,
      icon: <MarkUnreadChatAltIcon />,
      color: "#01BBAC",
      tooltip: renderGenericTooltip(
        tickets.queueAndCount?.unreadMessagesTooltip || ""
      ),
    },
    {
      title: (
        <Box display="flex" alignItems="center">
          {i18n.t("dashboard.cards.inAttendance")}
          <InfoIcon style={{ marginLeft: 5 }} />
        </Box>
      ),
      value: tickets.counters?.open || 0,
      icon: <QuestionAnswerIcon />,
      color: "#0b708c",
      tooltip: renderGenericTooltip(
        tickets.queueAndCount?.openTooltip || ""
      ),
    },
    {
      title: (
        <Box display="flex" alignItems="center">
          {i18n.t("dashboard.cards.waiting")}
          <InfoIcon style={{ marginLeft: 5 }} />
        </Box>
      ),
      value: tickets.counters?.pending || 0,
      icon: <PauseCircleFilledOutlinedIcon />,
      color: "#47606e",
      tooltip: renderGenericTooltip(
        tickets.queueAndCount?.pendingTooltip || ""
      ),
    },
    {
      title: i18n.t("dashboard.cards.chatbotTickets"),
      value: tickets.counters?.openChatbot || 0,
      icon: <SmartToyOutlinedIcon />,
      color: "#8a2c40",
    },
    {
      title: (
        <Box display="flex" alignItems="center">
          {i18n.t("dashboard.cards.passiveTickets")}
          <InfoIcon style={{ marginLeft: 5 }} />
        </Box>
      ),
      value: tickets.counters?.receptiveTickects || 0,
      icon: <QuestionAnswerIcon />,
      color: "#28C037",
      tooltip: renderGenericTooltip(
        tickets.queueAndCount?.receptiveTooltip || ""
      ),
    },
    {
      title: (
        <Box display="flex" alignItems="center">
          {i18n.t("dashboard.cards.finalized")}
          <InfoIcon style={{ marginLeft: 5 }} />
        </Box>
      ),
      value: tickets.counters?.closed || 0,
      icon: <TaskAltIcon />,
      color: "#5852ab",
      tooltip: renderGenericTooltip(
        tickets.queueAndCount?.closedTooltip || ""
      ),
    },
    {
      title: i18n.t("dashboard.cards.averageServiceTime"),
      value: tickets.average?.averageTimeTicket || "00h 00m",
      icon: <AlarmOnIcon />,
      color: "#F79009",
    },
    {
      title: i18n.t("dashboard.cards.totalReceivedMessages"),
      value: tickets.counters?.receivedMessages || 0,
      icon: <ChatIcon />,
      color: "#333133",
    },
    {
      title: i18n.t("dashboard.cards.totalSentMessages"),
      value: tickets.counters?.sentMessages || 0,
      icon: <SendIcon />,
      color: "#558a59",
    },
    {
      title: i18n.t("dashboard.cards.activeTickets"),
      value: tickets.counters?.campaign || 0,
      icon: <ScheduleSendIcon />,
      color: "#EE4512",
    },
  ];

  return (
    <Container className={classes.container}>
      <Grid xs={12} sm={6}></Grid>
      <Typography
        style={{
          margin: "20px 0px 20px 0px",
          color: "black",
          fontWeight: "bold",
        }}
        component="h1"
        variant="h5"
        gutterBottom
      >
        {`${i18n.t("dashboard.title")} ${user.name}!`}
      </Typography>
      <Typography
        style={{ marginBottom: "20px", color: "#9e9e9e" }}
      >{`${i18n.t("dashboard.updatedAt")} ${updatedAt}`}</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Container maxWidth="xl">
            <Grid2 container spacing={3}>
              {dashboardCards.map((card, index) => (
                <Grid2 key={index} xs={12} sm={6} lg={4}>
                  <MobileTooltip title={card.tooltip} placement="bottom">
                    <Card sx={{ height: "100%" }}>
                      <CardContent className={classes.cardContent}>
                        <Stack
                          alignItems="flex-start"
                          direction="row"
                          justifyContent="space-between"
                          spacing={0}
                        >
                          <Stack spacing={1}>
                            <Typography
                              color="primary"
                              variant="overline"
                              className={classes.overline}
                            >
                              {card.title}
                            </Typography>
                            <Typography variant="h4" className={classes.h4}>
                              {card.value}
                            </Typography>
                          </Stack>
                          <Avatar
                            sx={{
                              backgroundColor: card.color,
                              height: 55,
                              width: 55
                            }}
                          >
                            <SvgIcon>{card.icon}</SvgIcon>
                          </Avatar>
                        </Stack>
                      </CardContent>
                    </Card>
                  </MobileTooltip>
                </Grid2>
              ))}
            </Grid2>
          </Container>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;